import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Trophy, Gem, Camera, MapPin, KeyRound, Share2, Check, Play, Loader2, Music2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { chime } from "../lib/celebrate";
import { fileToDataUrl } from "../lib/community";
import { PlayerAvatar, PIECES } from "../components/party/PlayerAvatar";
import BoardView, { Dice, TurnBanner, ClueCard } from "../components/party/BoardView";
import { WheelDisc, spinTo, ClipPlayer, ytId, BrandMark } from "../components/party/GameVisuals";
import { PathRace, ShotGame, MysteryGame } from "../components/party/MoreGames";
import { TvBar } from "../components/party/TvShare";

const boom = () => confetti({ particleCount: 140, spread: 80, origin: { y: 0.6 }, colors: ["#C8A96B", "#ffffff", "#111111"] });

function Wheel({ g, me, act, refresh, setHold }) {
  const segs = g.config.segments;
  const [rot, setRot] = useState(0);
  const [result, setResult] = useState(me.done.includes("spin") ? "done" : null);
  const n = segs.length;
  const [spinning, setSpinning] = useState(false);
  async function spin() {
    setSpinning(true); setHold(true);
    const d = await act("spin", {}, { quiet: true });
    if (!d) { setSpinning(false); setHold(false); return; }
    setRot(spinTo(d.index, n));
    setTimeout(() => { setResult(d.segment); setSpinning(false); setHold(false); chime(true); boom(); refresh(); }, 4200);
  }
  return (
    <div className="text-center" data-testid="wheel-game">
      <WheelDisc segs={segs} rot={rot} />
      {!result && !spinning && <button onClick={spin} className="btn-gold mt-8" data-testid="wheel-spin-btn">Spin the ring</button>}
      {spinning && <p className="mt-8 text-white/70" data-testid="wheel-spinning">Spinning…</p>}
      <AnimatePresence>{result && result !== "done" && (
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mt-8" data-testid="wheel-result">
          <p className="font-script text-[var(--gold)] text-4xl">You landed on</p>
          <p className="font-serif text-3xl">{result.label}</p><p className="text-[var(--gold)] mt-1">+{result.points} points</p>
        </motion.div>)}
      </AnimatePresence>
      {result === "done" && <p className="mt-6 text-white/70" data-testid="wheel-done">You've already spun. Check the leaderboard!</p>}
    </div>
  );
}

function Called({ called }) {
  if (!called) return null;
  const last = called[called.length - 1];
  return (
    <div className="text-center mb-4" data-testid="bingo-called">
      <p className="text-[11px] uppercase tracking-[0.3em] text-white/50">{called.length ? `Call ${called.length}` : "No calls yet"}</p>
      {last ? <motion.p key={last} initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="font-serif text-3xl text-[var(--gold)]" data-testid="bingo-last-call">{last}</motion.p> : <p className="text-white/60 text-sm">Listen for the host's first call-out</p>}
      {called.length > 1 && <p className="text-[11px] text-white/50 mt-1 line-clamp-2" data-testid="bingo-call-history">{called.slice(0, -1).reverse().join(" · ")}</p>}
    </div>
  );
}

function Bingo({ g, me, act, setMe }) {
  const brand = g.config.brand;
  const marks = new Set(me.marks || []);
  const called = g.phase ? new Set(g.called || []) : null;
  async function tap(i) {
    if (i === 12) return;
    const d = await act("bingo", { index: i });
    if (!d) return;
    if (d.points > (me.points || 0) + 10) { chime(true); boom(); }
    setMe({ ...me, marks: d.marks, points: d.points });
  }
  return (
    <div className="max-w-md mx-auto">
      <Called called={g.phase ? g.called || [] : null} />
      {brand?.name && <p className="text-center text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: brand.color || "#C8A96B" }} data-testid="bingo-brand-name-display">Presented by {brand.name}</p>}
    <div className="grid grid-cols-5 gap-1.5" data-testid="bingo-card">
      {me.card.map((c, i) => {
        const on = i === 12 || marks.has(i);
        const live = !called || i === 12 || called.has(c);
        return (
          <motion.button whileTap={{ scale: 0.9 }} key={i} onClick={() => tap(i)} data-testid={`bingo-cell-${i}`} data-called={live ? "1" : "0"}
            className={`aspect-square rounded-xl p-1 text-[9px] sm:text-[11px] leading-tight flex items-center justify-center text-center transition-colors ${i === 12 ? "bg-[#0b0b0b] text-white" : on ? "bg-[var(--gold)] text-white" : live ? "bg-white/20 text-white ring-1 ring-[#C8A96B]/70" : "bg-white/5 text-white/45"}`}
            style={i === 12 ? { boxShadow: `inset 0 0 0 2px ${brand?.color || "#C8A96B"}` } : on && brand?.color ? { background: brand.color } : undefined}>
            {i === 12 ? <span className="flex flex-col items-center gap-0.5" data-testid="bingo-free-logo"><img src={brand?.logo_url || "/brand/logo-mark.png"} alt="" className="h-7 w-auto max-w-full object-contain" /><span className="text-[8px] tracking-[0.15em]" style={{ color: brand?.color || "#C8A96B" }}>FREE</span></span> : c}
          </motion.button>
        );
      })}
    </div>
    </div>
  );
}

const HUNT_ICON = { photo: Camera, social: Share2, gps: MapPin, code: KeyRound };

function HuntTask({ t, done, act }) {
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [link, setLink] = useState("");
  const fileRef = useRef(null);
  const Icon = HUNT_ICON[t.kind] || Camera;
  async function submit(extra) {
    setBusy(true);
    const d = await act("hunt", { task_id: t.task_id, ...extra });
    setBusy(false);
    if (d) { chime(true); boom(); }
  }
  async function photo(e) { try { await submit({ photo_data_url: await fileToDataUrl(e.target.files[0]), proof_url: link || undefined }); } catch (err) { toast.error(err.message); } e.target.value = ""; }
  function gps() {
    if (!navigator.geolocation) return toast.error("Location isn't available on this device");
    setBusy(true);
    navigator.geolocation.getCurrentPosition((p) => submit({ lat: p.coords.latitude, lng: p.coords.longitude }), () => { setBusy(false); toast.error("Allow location access to check in"); }, { enableHighAccuracy: true, timeout: 15000 });
  }
  return (
    <div className={`rounded-2xl p-4 border ${done ? "border-[var(--gold)] bg-[#C8A96B]/10" : "border-white/10 bg-white/5"}`} data-testid={`hunt-task-${t.task_id}`}>
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">{done ? <Check className="w-5 h-5 text-[var(--gold)]" /> : <Icon className="w-5 h-5 text-[var(--gold)]" />}</span>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-lg leading-tight">{t.title} <span className="text-[var(--gold)] text-sm">+{t.points}</span></p>
          <p className="text-xs text-white/60">{t.hint}{t.place_name && ` · ${t.place_name}`}</p>
          {!done && (
            <div className="mt-3 flex flex-wrap gap-2">
              {t.kind === "social" && t.url && <a href={t.url} target="_blank" rel="noreferrer" className="text-xs px-3 py-2 rounded-full bg-white/10">Open the post</a>}
              {t.kind === "social" && <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste your share link" className="flex-1 min-w-0 bg-white/10 rounded-full px-3 py-2 text-xs outline-none" data-testid={`hunt-link-${t.task_id}`} />}
              {t.kind === "social" && link && <button disabled={busy} onClick={() => submit({ proof_url: link })} className="text-xs px-3 py-2 rounded-full bg-[var(--gold)]" data-testid={`hunt-link-submit-${t.task_id}`}>Submit link</button>}
              {(t.kind === "photo" || t.kind === "social") && <button disabled={busy} onClick={() => fileRef.current?.click()} className="text-xs px-3 py-2 rounded-full bg-[var(--gold)] flex items-center gap-1" data-testid={`hunt-photo-${t.task_id}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}{t.kind === "social" ? "Screenshot" : "Snap photo"}</button>}
              {t.kind === "gps" && <button disabled={busy} onClick={gps} className="text-xs px-3 py-2 rounded-full bg-[var(--gold)] flex items-center gap-1" data-testid={`hunt-gps-${t.task_id}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}Check in here</button>}
              {t.kind === "code" && <><input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter code" className="w-32 bg-white/10 rounded-full px-3 py-2 text-xs outline-none uppercase" data-testid={`hunt-code-${t.task_id}`} /><button disabled={busy || !code} onClick={() => submit({ code })} className="text-xs px-3 py-2 rounded-full bg-[var(--gold)]" data-testid={`hunt-code-submit-${t.task_id}`}>Unlock</button></>}
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={photo} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Song({ g, me, act }) {
  const [res, setRes] = useState({});
  const [playing, setPlaying] = useState(null);
  return (
    <div className="space-y-6" data-testid="song-game">
      {g.config.rounds.map((r) => {
        const answered = res[r.round_id] || (me.done.includes(`song:${r.round_id}`) && { done: true });
        const hasClip = !!ytId(r.clip_url);
        return (
          <div key={r.round_id}>
            <p className="font-serif text-2xl mb-3">{r.prompt}</p>
            <ClipPlayer r={r} />
            <div className="grid grid-cols-2 gap-3">
              {r.options.map((o, i) => {
                const id = hasClip ? null : ytId(o.youtube_url);
                const correctIdx = answered?.correct_index ?? r.revealed_index;
                const state = correctIdx === i ? "ring-4 ring-emerald-400" : answered?.choice === i ? (answered?.pending_reveal ? "ring-4 ring-[var(--gold)]" : "ring-4 ring-rose-400") : "";
                return (
                  <div key={i} className={`rounded-2xl overflow-hidden bg-white/5 ${state}`} data-testid={`song-option-${r.round_id}-${i}`}>
                    {hasClip ? null : playing === `${r.round_id}-${i}` && id ? (
                      <iframe title={o.label} className="w-full aspect-video" src={`https://www.youtube.com/embed/${id}?autoplay=1`} allow="autoplay; encrypted-media" allowFullScreen />
                    ) : (
                      playing === `${r.round_id}-${i}` ? <div className="w-full aspect-video bg-black flex items-center justify-center text-xs text-white/60 px-3 text-center" data-testid={`song-novideo-${r.round_id}-${i}`}>The couple hasn't added this song's video yet</div> :
                      <button onClick={() => setPlaying(`${r.round_id}-${i}`)} className="relative w-full aspect-video bg-black block" data-testid={`song-play-${r.round_id}-${i}`}>
                        {id ? <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt="" className="w-full h-full object-cover opacity-80" /> : <span className="absolute inset-0 bg-gradient-to-br from-[#C8A96B]/40 to-black" />}
                        <span className="absolute inset-0 flex items-center justify-center"><span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center"><Play className="w-5 h-5 text-black fill-black ml-0.5" /></span></span>
                      </button>
                    )}
                    <div className="p-2.5">
                      <p className="text-xs leading-tight line-clamp-2">{o.label}</p>
                      {!answered && <button onClick={async () => { const d = await act("song", { round_id: r.round_id, choice: i }); if (d) { setRes({ ...res, [r.round_id]: { ...d, choice: i } }); if (!d.pending_reveal) { chime(d.correct); if (d.correct) boom(); } } }} className="mt-2 w-full text-[11px] uppercase tracking-wider py-2 rounded-full bg-[var(--gold)]" data-testid={`song-pick-${r.round_id}-${i}`}>This one</button>}
                    </div>
                  </div>
                );
              })}
            </div>
            {answered?.pending_reveal && <p className="text-[var(--gold)] text-sm mt-2" data-testid={`song-locked-${r.round_id}`}>Locked in! Watch the big screen for the reveal.</p>}
            {answered?.done && r.revealed_index === undefined && <p className="text-white/60 text-sm mt-2">{r.held ? "Locked in! Watch the big screen for the reveal." : "You've answered this round."}</p>}
            {r.revealed_index !== undefined && <p className="text-emerald-300 text-sm mt-2" data-testid={`song-revealed-${r.round_id}`}>Revealed: {r.options[r.revealed_index]?.label}</p>}
          </div>
        );
      })}
    </div>
  );
}

function BoardGame({ g, me, act, load, loadMe }) {
  const [rolling, setRolling] = useState(false);
  const [result, setResult] = useState(null);
  const st = g.state;
  const myTurn = st && st.order[st.turn % st.order.length] === me.player_id;
  const myClue = st?.phase === "answer" && st.pending?.player_id === me.player_id;
  const cards = st?.cards?.[me.player_id] || [];
  async function roll() {
    setRolling(true);
    const d = await act("board/roll", {}, { quiet: true });
    setTimeout(() => { setRolling(false); if (d) chime(true); load(); loadMe(); }, 900);
  }
  async function pick(i) {
    const d = await act("board/answer", { choice: i }, { quiet: true });
    if (!d) return;
    setResult({ choice: i, correct_index: d.correct_index });
    chime(d.correct); if (d.correct) boom();
    setTimeout(() => { setResult(null); load(); loadMe(); }, 1800);
  }
  return (
    <div className="space-y-4" data-testid="board-game">
      <TurnBanner g={g} me={me} />
      <BoardView g={g} rolling={rolling} />
      {(myClue || result) ? <ClueCard g={g} onPick={result ? null : pick} result={result} />
        : st?.phase === "answer" && <ClueCard g={g} />}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-white/60" data-testid="board-my-cards">Clue cards: <b className="text-[var(--gold)]">{cards.length} / {g.config.clues.length}</b></p>
        <button disabled={!myTurn || st.phase !== "roll" || rolling} onClick={roll} className="btn-gold disabled:opacity-40" data-testid="board-roll-btn"><Dice value={st?.last_roll?.value || 6} size={18} /> {rolling ? "Rolling…" : "Roll the dice"}</button>
      </div>
      {st?.log?.length > 0 && <ul className="text-xs text-white/50 space-y-0.5" data-testid="board-log">{st.log.slice(0, 4).map((l, i) => <li key={i}>{l}</li>)}</ul>}
    </div>
  );
}

function JoinForm({ code, g, onJoined }) {
  const [name, setName] = useState("");
  const [piece, setPiece] = useState("ring");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  async function join(e) {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post(`/public/party/${code}/join`, { name, piece, photo_data_url: photo }); onJoined(data); }
    catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  }
  if (g.type === "board" && g.missing?.length) return <p className="text-center text-white/70 py-10" data-testid="board-not-ready">The couple is still setting up this game. Check back soon!</p>;
  return (
    <form onSubmit={join} className="max-w-sm mx-auto text-center space-y-4" data-testid="party-join-form">
      <label className="block mx-auto w-20 h-20 rounded-full bg-white/10 overflow-hidden cursor-pointer flex items-center justify-center" data-testid="party-photo-label">
        {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : <Camera className="w-6 h-6 text-white/60" />}
        <input type="file" accept="image/*" capture="user" className="hidden" data-testid="party-photo-input" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setPhoto(await fileToDataUrl(f)); }} />
      </label>
      <p className="text-[11px] text-white/50 -mt-2">Add a profile photo (optional)</p>
      <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={40} placeholder="Your name" className="w-full bg-white/10 rounded-full px-5 py-3 text-center outline-none" data-testid="party-name-input" />
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2">Pick your game piece</p>
        <div className="flex flex-wrap justify-center gap-2">
          {Object.entries(PIECES).map(([k, Icon]) => <button type="button" key={k} onClick={() => setPiece(k)} data-testid={`piece-${k}`} className={`w-10 h-10 rounded-full flex items-center justify-center ${piece === k ? "bg-[var(--gold)] text-black" : "bg-white/10"}`}><Icon className="w-5 h-5" /></button>)}
        </div>
      </div>
      <button disabled={busy} className="btn-gold w-full justify-center" data-testid="party-join-btn">{busy ? "Joining…" : "Join the fun"}</button>
    </form>
  );
}

function Leaderboard({ g }) {
  const [tab, setTab] = useState("game");
  const rows = tab === "game" ? g.leaderboard : g.party_leaderboard;
  return (
    <div className="bg-white/5 rounded-3xl p-5" data-testid="party-leaderboard">
      <div className="flex gap-1 mb-4">{[["game", "This game"], ["party", "Whole party"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`leaderboard-tab-${k}`} className={`px-4 py-1.5 rounded-full text-xs ${tab === k ? "bg-[var(--gold)]" : "text-white/60"}`}>{l}</button>)}</div>
      {rows.length === 0 && <p className="text-sm text-white/50">No points yet. Be the first!</p>}
      <ol className="space-y-2">
        {rows.slice(0, 10).map((r, i) => (
          <motion.li layout key={r.name + i} className="flex items-center gap-3" data-testid={`leaderboard-row-${i}`}>
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${i < 3 ? "bg-[var(--gold)]" : "bg-white/10"}`}>{i === 0 ? <Trophy className="w-3.5 h-3.5" /> : i + 1}</span>
            <PlayerAvatar p={r} size={26} />
            <span className="flex-1 truncate">{r.name}</span><span className="font-serif text-lg">{r.points}</span>
          </motion.li>
        ))}
      </ol>
    </div>
  );
}

export default function PartyPlayPage() {
  const { code } = useParams();
  const key = `lif_party_${code?.toUpperCase()}`;
  const [g, setG] = useState(null);
  const [err, setErr] = useState("");
  const [me, setMe] = useState(null);
  const creds = () => JSON.parse(localStorage.getItem(key) || "null");
  const hold = useRef(false);

  const load = useCallback(() => api.get(`/public/party/${code}`).then((r) => { if (!hold.current) setG(r.data); }).catch((e) => setErr(formatApiError(e))), [code]);
  const loadMe = useCallback(() => { const c = creds(); if (c) api.post(`/public/party/${code}/me`, c).then((r) => setMe(r.data)).catch(() => localStorage.removeItem(key)); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  const fast = !!g?.state;
  useEffect(() => { load(); loadMe(); const t = setInterval(load, fast ? 2000 : 5000); return () => clearInterval(t); }, [load, loadMe, fast]);

  function joined(data) { localStorage.setItem(key, JSON.stringify({ player_id: data.player_id, token: data.token })); loadMe(); load(); }
  async function act(path, body = {}, { quiet = false } = {}) {
    try { const { data } = await api.post(`/public/party/${code}/${path}`, { ...creds(), ...body }); if (!quiet) { load(); if (path !== "bingo") loadMe(); } return data; }
    catch (e) { chime(false); toast.error(formatApiError(e)); return null; }
  }

  if (err) return <div className="min-h-screen bg-[var(--ink)] text-white flex flex-col items-center justify-center p-6 text-center"><p className="font-serif text-3xl" data-testid="party-error">We couldn't find that game.</p><a href="/play/DEMO26" className="btn-gold mt-6">Play the demo game</a></div>;
  if (!g) return <div className="min-h-screen bg-[var(--ink)]" />;
  const boardOver = !!g.state && g.state.phase === "over";
  const More = { candy: PathRace, wife: PathRace, hubby: PathRace, shot: ShotGame, mystery: MysteryGame }[g.type];
  return (
    <div className="min-h-screen bg-[var(--ink)] text-white pb-16" data-testid="party-play-page">
      <div className="max-w-5xl mx-auto px-5 pt-8">
        <div className="flex justify-center mb-4"><BrandMark /></div>
        <p className="font-script text-[var(--gold)] text-3xl text-center">{g.couple_names}</p>
        <h1 className="font-serif text-4xl md:text-5xl text-center font-light">{g.title}</h1>
        {me && <p className="text-center text-sm text-white/60 mt-2" data-testid="party-my-points">Playing as {me.name} · <b className="text-[var(--gold)]">{g.state?.points?.[me.player_id] != null ? g.state.points[me.player_id] : me.points} pts</b></p>}
        <div className="mt-4"><TvBar g={g} /></div>
        {!g.is_open && !boardOver && <p className="text-center text-rose-300 mt-2">This game is closed.</p>}
        <div className="grid lg:grid-cols-[1fr_320px] gap-8 mt-8">
          <div>
            {boardOver ? <div className="space-y-4" data-testid="board-final">{g.type === "board" ? <><TurnBanner g={g} me={me} /><BoardView g={g} /></> : <More g={g} me={me} act={act} load={load} />}</div>
            : !me && !g.is_open ? <p className="text-center text-white/70 py-10" data-testid="party-closed">The couple has closed this game. See the final scores on the leaderboard.</p>
            : !me ? <JoinForm code={code} g={g} onJoined={joined} />
            : !g.is_open ? <p className="text-center text-white/70 py-10" data-testid="party-closed">The couple has closed this game. See the final scores on the leaderboard.</p>
              : g.phase === "lobby" ? <div className="text-center py-14 space-y-3" data-testid="party-lobby"><p className="font-serif text-3xl">You're in!</p><p className="text-white/70">Waiting for the host to tap <b className="text-[var(--gold)]">Start</b>…</p><p className="text-xs text-white/50" data-testid="party-lobby-count">{(g.players || []).length} player{(g.players || []).length === 1 ? "" : "s"} ready</p><span className="inline-block w-3 h-3 rounded-full bg-[var(--gold)] animate-ping" /></div>
              : g.phase === "ended" ? <p className="text-center text-white/70 py-10" data-testid="party-ended">The host has ended this game. Check the final scores on the leaderboard.</p>
              : g.type === "wheel" ? <Wheel g={g} me={me} act={act} setHold={(v) => { hold.current = v; }} refresh={() => { load(); loadMe(); }} />
              : g.type === "bingo" ? <Bingo g={g} me={me} act={act} setMe={setMe} />
              : g.type === "board" ? <BoardGame g={g} me={me} act={act} load={load} loadMe={loadMe} />
              : More ? <More g={g} me={me} act={act} load={load} />
              : g.type === "hunt" ? <div className="space-y-3">{g.config.tasks.map((t) => <HuntTask key={t.task_id} t={t} done={me.done.includes(`hunt:${t.task_id}`)} act={act} />)}</div>
              : <Song g={g} me={me} act={act} />}
          </div>
          <Leaderboard g={g} />
        </div>
      </div>
    </div>
  );
}

